// Equipment on site for the Daily Force Report's Equipment sheet.
//
// The source is project controls' PM tracker (readEquipmentLog: "PM Tracker
// <job>.xlsx", Equipment Log sheet), one row per rental PO line, purchase or
// company unit. The client sheet is a count per rate sheet item and nothing
// else (Seth 10/30: "the equipment classification based on rate sheet and the
// count and that's it"), so each log line is named the way Attachment B names
// it and its units are added up.
//
// A line counts on a day when the log says it is on site (Onsite = Yes), its
// PO is not Closed, it went on rent on or before the day (or has no date) and
// has not come off rent by it. Onsite Y/N is the log's state today, so a
// report for a past day is only as right as the log was kept.
//
// Lines no rule names (office trailers, toilets, radios, crane mats, pickups
// the log doesn't size) stay off the client sheet and come back in `unmapped`
// so the caller can say so rather than quietly drop them.

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Log "Equipment Type" -> rate sheet item. null: on purpose not a rate sheet
// item (rigging plans, torque wrenches rented through the tool trailer).
// First match wins, so the specific rules sit above the general ones.
export const LOG_RULES = [
    [/rigging equipment|lift plan/i, null],
    [/torque wrench/i, null],
    [/600 ?-?t(on)?\b|crawler.*600/i, 'Crane - 600 Ton Crawler'],
    [/crane.*160|160 ?-?t(on)?\b.*crane/i, 'Crane - 160 Ton Rough Terrain'],
    [/crane.*1(0[1-9]|1\d|2\d|30) ?-?ton|120 ?-?ton/i, 'Crane - 101-130 Ton Rough Terrain'],
    [/(manlift|boom|lift).*135|135.*(manlift|boom|lift)/i, "Man lift - 135' Lift"],
    [/(manlift|boom).*\b80\b|\b80'.*articulating/i, "Man lift - 80' - Articulating Boom Lift"],
    [/articulating boom (4[5-9]|5\d)|manlift.*\b50 ?ft/i, "Man lift - 45' - 59' - Articulating Boom Lift"],
    // The billing tables price the log's 37' electric scissors on the 40'-54' row.
    [/scissor/i, "Man lift - 40' - 54' Scissor Lift"],
    [/forklift.*(12 ?k|53-69)/i, 'Forklift - Telehandler - 10k to 12k lb Cap.'],
    [/trackhoe 24[56]/i, 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)'],
    [/backhoe/i, 'Rubber Tire Backhoe (Machine with Standard Bucket)'],
    [/mini excavator|excavator.*2 ?-? ?6 ?-?ton/i, 'Excavator - 2 to 6 Metric Ton (Machine with Standard Bucket)'],
    [/trench roller/i, 'Trench Roller Compactor'],
    [/mortar mixer|cement mixer/i, 'Portable Cement Mixer'],
    [/trailer.*water|water trailer/i, 'Trailer - Water'],
    [/generator.*25 ?(kva|kw)/i, 'Generator - 25kva'],
    [/generator.*(6500|5,?500|honda|kobalt)/i, 'Generator - 5.0 to 7.5 KW'],
    [/light (plant|tower)/i, 'Light Plant - 6.0 - 7.5 KW'],
    [/compressor.*185|185 ?c(f)?m/i, 'Air Compressors - 185 CFM'],
    [/weld.*300 ?amp/i, 'Welding Machine - 300 Amp'],
    [/53' van trailer|tool van/i, "Tool Van - 40' - 53' Trailer"],
    [/enclosed cargo trailer|trailer cargo/i, "Trailer - 12' - 24' Enclosed Tool Trailer"],
    [/gooseneck/i, 'Trailer - Gooseneck Tandem Axle'],
    // Ford T3LP = Transit 350 passenger wagon (VIN 1FBAX2…).
    [/ford t3lp|transit|passenger van/i, 'Bus - 15 Passenger Van'],
    [/f-?150/i, 'Truck - 1/2 Ton'],
    [/f-?250/i, 'Truck - 3/4 Ton'],
    [/f-?550/i, 'Truck - 1 1/2 Ton (Double Rear Wheel)'],
    [/utv/i, 'UTV / 4x4 side by side / 8x8 amphibious'],
    [/1224/i, '1224 Rigid Threader 1/2"- 4"'],
    [/535/i, '535 Rigid Threader 1/2"- 2"'],
    [/greenlee\s*854/i, 'Electric Conduit Bender 1/2" to 2"'],
];

/** The rate sheet item a log line is, '' when no rule names it, null when a
 *  rule says it is not a rate sheet item. */
export const logRateItem = (type) => {
    const hit = LOG_RULES.find(([re]) => re.test(String(type || '')));
    return hit ? hit[1] : '';
};

/**
 * Units on a log line. The log writes the count into the name as often as
 * into "QTY of Units" ("Portable Toilets - 12" with a qty of 1, "Threaders
 * 4- units"), and gives company units a qty of 0, so the name's count wins,
 * then a positive qty, then 1.
 */
export const logUnits = (type, qty) => {
    const s = String(type || '').trim();
    for (const re of [/\(\s*(\d+)\s*\)/, /qty\s*-?\s*(\d+)/i, /(\d+)\s*-?\s*units?\b/i, /-\s*(\d+)\s*$/]) {
        const m = re.exec(s);
        if (m && Number(m[1]) > 0) return Number(m[1]);
    }
    const q = Number(qty);
    return Number.isFinite(q) && q > 0 ? Math.round(q) : 1;
};

/** Is the line on site on `date` ('YYYY-MM-DD')? See the header. */
export const onSiteOn = (row, date) => {
    if (!/^y/i.test(String(row?.onsite || '').trim())) return false;
    if (/^closed/i.test(String(row?.status || '').trim())) return false;
    if (row.onRent && row.onRent > date) return false;
    if (row.offRent && row.offRent <= date) return false;
    return true;
};

/**
 * The day's rollup: items [{ name, count }] by rate sheet item, A-Z, and
 * unmapped [{ type, units }] for on-site lines no rule names.
 *   equipAs  billingContract equipRowAs(job): rows the job's Service Order
 *            bills as another row (PB4 bills the UTV row as "ATV - 4 Wheel").
 */
export const equipLogRollup = (rows, date, { equipAs = {} } = {}) => {
    const counts = {};
    const unmapped = [];
    (rows || []).filter((r) => r && r.type && onSiteOn(r, date)).forEach((r) => {
        const units = logUnits(r.type, r.qty);
        const item = logRateItem(r.type);
        if (item === null) return;
        if (!item) { unmapped.push({ type: r.type, units }); return; }
        const name = equipAs[item] || item;
        counts[name] = (counts[name] || 0) + units;
    });
    const items = Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => norm(a.name).localeCompare(norm(b.name)));
    return { items, unmapped };
};

export const equipUnmappedWarning = (unmapped) => {
    const n = (unmapped || []).length;
    return n
        ? `${n} equipment log ${n === 1 ? 'line is' : 'lines are'} on site but not on the rate sheet and left off the Equipment sheet: `
            + unmapped.map((u) => u.type).join('; ')
        : null;
};
